import type { ProjectType, QuestionDefinition, QuestionSet } from "@reno-estimator/core";
import { commonQuestions } from "./common-questions.js";
import { kitchenQuestions } from "./kitchen.js";
import { bathroomQuestions } from "./bathroom.js";
import { basementQuestions } from "./basement.js";
import { additionQuestions } from "./addition.js";
import { deckPatioQuestions } from "./deck-patio.js";
import { livingRoomQuestions } from "./living-room.js";
import { familyRoomQuestions } from "./family-room.js";
import { diningRoomQuestions } from "./dining-room.js";
import { bedroomQuestions } from "./bedroom.js";
import { primaryBedroomQuestions } from "./primary-bedroom.js";
import { homeOfficeQuestions } from "./home-office.js";
import { laundryRoomQuestions } from "./laundry-room.js";
import { mudroomQuestions } from "./mudroom.js";
import { entrywayFoyerQuestions } from "./entryway-foyer.js";
import { hallwayQuestions } from "./hallway.js";
import { garageQuestions } from "./garage.js";
import { atticQuestions } from "./attic.js";
import { exteriorQuestions } from "./exterior.js";

/**
 * Per-project-type question sets. kitchen, bathroom/ensuite, and
 * basement/basement_suite have project-specific questions (see
 * docs/unresolved-decisions.md for the other 17, which still only get the
 * common set below). Adding a new project type's questions is exactly
 * this: write a `<project-type>.ts` file following kitchen.ts's pattern
 * and add it to this map - no wizard/UI code needs to change (see
 * docs/estimator-ui.md).
 */
const projectSpecificQuestions: Partial<Record<ProjectType, QuestionDefinition[]>> = {
  kitchen: kitchenQuestions,
  bathroom: bathroomQuestions,
  ensuite: bathroomQuestions,
  basement: basementQuestions,
  basement_suite: basementQuestions,
  addition: additionQuestions,
  deck_patio: deckPatioQuestions,
  living_room: livingRoomQuestions,
  family_room: familyRoomQuestions,
  dining_room: diningRoomQuestions,
  bedroom: bedroomQuestions,
  primary_bedroom: primaryBedroomQuestions,
  home_office: homeOfficeQuestions,
  laundry_room: laundryRoomQuestions,
  mudroom: mudroomQuestions,
  entryway_foyer: entrywayFoyerQuestions,
  hallway: hallwayQuestions,
  garage: garageQuestions,
  attic: atticQuestions,
  exterior: exteriorQuestions
};

export function getQuestionSetForProjectType(projectType: ProjectType): QuestionSet {
  const specific = projectSpecificQuestions[projectType] ?? [];
  const questions = [...commonQuestions, ...specific].sort((a, b) => a.order - b.order);
  return {
    version: "0.1.0-phase0",
    questions
  };
}

export function getAllQuestionDefinitions(): QuestionDefinition[] {
  const all = new Map<string, QuestionDefinition>();
  for (const q of commonQuestions) all.set(q.id, q);
  for (const set of Object.values(projectSpecificQuestions)) {
    for (const q of set ?? []) all.set(q.id, q);
  }
  return [...all.values()];
}
